import { useState, useEffect, useRef, useCallback } from "react";
import { tenders } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Search, X, Upload, Sparkles, ArrowRight, Hash, FileText, Download, Shield, AlertTriangle, CheckCircle, XCircle, BarChart3 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { useNavigate } from "react-router-dom";

const statusColor = {
  New: "bg-[#2ECC71]/10 text-[#2ECC71] border-[#2ECC71]/20",
  Bidding: "bg-[#FFB800]/10 text-[#FFB800] border-[#FFB800]/20",
  Won: "bg-[#FF5C1A]/10 text-[#FF5C1A] border-[#FF5C1A]/20",
  Lost: "bg-[#E74C3C]/10 text-[#E74C3C] border-[#E74C3C]/20",
};

const NICHES = ["Civil Construction", "Electrical", "Plumbing", "Road & Highway", "Building Renovation", "Water Supply", "Drainage", "Irrigation", "Government Furniture", "IT Infrastructure", "Other"];
const states = ["Uttar Pradesh", "Bihar", "Madhya Pradesh", "Maharashtra", "Rajasthan", "Karnataka", "Tamil Nadu", "Gujarat"];
const statuses = ["New", "Bidding", "Won", "Lost"];
const portals = ["GeM", "CPWD", "NIC", "State Portal", "PWD", "Other"];

const formatCurrency = (val) => {
  if (val >= 10000000) return `${(val / 10000000).toFixed(1)} Cr`;
  if (val >= 100000) return `${(val / 100000).toFixed(1)} L`;
  if (val >= 1000) return `${(val / 1000).toFixed(0)}K`;
  return val?.toLocaleString("en-IN") || "0";
};

export default function TenderRadar() {
  const [tenderList, setTenderList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [bidSearchOpen, setBidSearchOpen] = useState(false);
  const [summaryDialogOpen, setSummaryDialogOpen] = useState(false);
  const [analyzeDialogOpen, setAnalyzeDialogOpen] = useState(false);
  const [filterCategory, setFilterCategory] = useState("");
  const [filterState, setFilterState] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [search, setSearch] = useState("");
  const [showAllTenders, setShowAllTenders] = useState(false);
  const [summaryText, setSummaryText] = useState("");
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [parseLoading, setParseLoading] = useState(false);
  const [parsedData, setParsedData] = useState(null);
  const [analyzeLoading, setAnalyzeLoading] = useState(false);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [bidSearchForm, setBidSearchForm] = useState({ bid_number: "", portal: "", state: "", district: "", closing_date: "" });
  const [bidSearchResult, setBidSearchResult] = useState(null);
  const [bidSearching, setBidSearching] = useState(false);
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const user = JSON.parse(localStorage.getItem("contractor_os_user") || "{}");
  const userNiche = user.work_niche || "";

  const [form, setForm] = useState({
    title: "", category: userNiche || "General", state: "", value: "", status: "New",
    deadline: "", source: "Manual", description: "", bid_number: "", portal: "", district: "", eligibility: ""
  });

  const fetchTenders = useCallback(async () => {
    try {
      const params = { niche_filter: showAllTenders ? "false" : "true" };
      if (filterCategory) params.category = filterCategory;
      if (filterState) params.state = filterState;
      if (filterStatus) params.status = filterStatus;
      const r = await tenders.list(params);
      setTenderList(r.data);
    } catch (err) { toast.error("Failed to load tenders"); } finally { setLoading(false); }
  }, [showAllTenders, filterCategory, filterState, filterStatus]);

  useEffect(() => { fetchTenders(); }, [fetchTenders]);

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await tenders.create({ ...form, value: parseFloat(form.value) || 0 });
      toast.success("Tender added");
      setAddDialogOpen(false);
      setForm({ title: "", category: userNiche || "General", state: "", value: "", status: "New", deadline: "", source: "Manual", description: "", bid_number: "", portal: "", district: "", eligibility: "" });
      fetchTenders();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add tender");
    }
  };

  const handleStatusChange = async (id, newStatus) => {
    try { await tenders.update(id, { status: newStatus }); fetchTenders(); } catch (err) { toast.error("Failed to update"); }
  };
  const handleDelete = async (id) => {
    try { await tenders.remove(id); toast.success("Deleted"); fetchTenders(); } catch (err) { toast.error("Failed"); }
  };
  const clearFilters = () => { setFilterCategory(""); setFilterState(""); setFilterStatus(""); };

  // Doc upload
  const handleDocUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error("Max 10MB"); return; }
    setParseLoading(true); setParsedData(null);
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        const b64 = reader.result.split(",")[1];
        const r = await tenders.parseDocument({ file_data: b64, file_type: file.type });
        if (r.data.success) { setParsedData(r.data.parsed); toast.success("Parsed!"); }
        else toast.error(r.data.error || "Parse failed");
      } catch (err) { toast.error("Upload failed"); }
      setParseLoading(false);
    };
    reader.readAsDataURL(file);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer?.files?.[0];
    if (file && fileInputRef.current) {
      const dt = new DataTransfer(); dt.items.add(file);
      fileInputRef.current.files = dt.files;
      handleDocUpload({ target: { files: dt.files } });
    }
  };

  const saveParsedTender = async () => {
    if (!parsedData) return;
    try {
      await tenders.create({ title: parsedData.title, category: parsedData.category || userNiche || "General", state: parsedData.state, value: parsedData.value || 0, status: "New", deadline: parsedData.deadline, source: parsedData.portal || "Document Upload", description: parsedData.description, bid_number: parsedData.bid_number, portal: parsedData.portal, district: parsedData.district, eligibility: parsedData.eligibility });
      toast.success("Saved!"); setUploadDialogOpen(false); setParsedData(null); fetchTenders();
    } catch (err) { toast.error("Failed to save"); }
  };

  // Summary
  const handleSummarize = async (tid) => {
    setSummaryLoading(true); setSummaryText(""); setSummaryDialogOpen(true);
    try {
      const r = await tenders.summarize({ tender_id: tid });
      setSummaryText(r.data.success ? r.data.summary : "Failed: " + (r.data.error || ""));
    } catch (err) { setSummaryText("Failed to generate summary"); }
    setSummaryLoading(false);
  };

  // Analyze & Prepare Bid
  const handleAnalyzeBid = async (tid) => {
    setAnalyzeLoading(true); setAnalysisResult(null); setAnalyzeDialogOpen(true);
    try {
      const r = await tenders.analyzeBid({ tender_id: tid });
      if (r.data.success) { setAnalysisResult(r.data); }
      else { toast.error(r.data.error || "Analysis failed"); setAnalyzeDialogOpen(false); }
    } catch (err) {
      const msg = err.response?.data?.detail || "Analysis failed";
      toast.error(err.response?.status === 403 ? "Upgrade to Professional — WhatsApp us at +91 98765 43210" : msg, { duration: 6000 });
      setAnalyzeDialogOpen(false);
    }
    setAnalyzeLoading(false);
  };

  // Download PDF
  const handleDownloadPdf = async () => {
    if (!analysisResult) return;
    setPdfLoading(true);
    try {
      const r = await tenders.generatePdf({ analysis_data: analysisResult });
      if (r.data.success) {
        const bytes = atob(r.data.pdf_data);
        const arr = new Uint8Array(bytes.length);
        for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
        const blob = new Blob([arr], { type: "application/pdf" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a"); a.href = url;
        a.download = `Tender_Analysis_${analysisResult.tender_title?.replace(/\s/g, '_') || 'report'}.pdf`;
        a.click(); URL.revokeObjectURL(url);
        toast.success("PDF downloaded!");
      } else { toast.error(r.data.error || "PDF failed"); }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upgrade to Professional — WhatsApp us at +91 98765 43210");
    }
    setPdfLoading(false);
  };

  // Bid Search
  const handleBidSearch = async (e) => {
    e.preventDefault(); setBidSearching(true); setBidSearchResult(null);
    try { const r = await tenders.searchBid(bidSearchForm); setBidSearchResult(r.data); } catch (err) { toast.error("Search failed"); }
    setBidSearching(false);
  };
  const addBidFromSearch = () => {
    if (!bidSearchResult) return;
    const pf = bidSearchResult.prefill || {};
    setForm({ ...form, bid_number: pf.bid_number || bidSearchForm.bid_number, portal: pf.portal || bidSearchForm.portal, state: pf.state || bidSearchForm.state, district: pf.district || bidSearchForm.district, deadline: pf.deadline || bidSearchForm.closing_date, source: pf.source || pf.portal || "Manual", category: userNiche || "General" });
    setBidSearchOpen(false); setAddDialogOpen(true);
  };

  // Convert to Project
  const handleConvertToProject = async (tid) => {
    try {
      const r = await tenders.convertToProject(tid);
      sessionStorage.setItem("project_prefill", JSON.stringify(r.data.prefill));
      navigate("/app/projects"); toast.success("Opening project form...");
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const filtered = tenderList.filter((t) => !search || t.title.toLowerCase().includes(search.toLowerCase()));
  const inputCls = "w-full bg-[#0A0A08] border border-[#27272A] text-[#F5F2E8] px-3 py-2.5 text-sm focus:border-[#FF5C1A] focus:ring-1 focus:ring-[#FF5C1A] outline-none transition-colors placeholder:text-[#444]";

  const DocStatusIcon = ({ status }) => {
    if (status === "ready") return <CheckCircle size={14} className="text-[#2ECC71]" />;
    if (status === "expiring") return <AlertTriangle size={14} className="text-[#FFB800]" />;
    return <XCircle size={14} className="text-[#E74C3C]" />;
  };

  return (
    <div className="space-y-6" data-testid="tender-radar">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h2 className="font-heading text-3xl tracking-[2px]">TENDER RADAR</h2>
          <p className="text-sm text-[#888880] mt-1">{userNiche && !showAllTenders ? `Showing ${userNiche} tenders` : "Showing all tenders"}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {/* Upload Doc */}
          <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
            <DialogTrigger asChild>
              <button data-testid="upload-tender-doc-btn" className="bg-[#111110] border border-[#27272A] text-[#F5F2E8] px-4 py-2.5 font-mono text-xs font-bold tracking-widest uppercase hover:border-[#FF5C1A] transition-colors flex items-center gap-2">
                <Upload size={14} /> Upload Doc
              </button>
            </DialogTrigger>
            <DialogContent className="bg-[#111110] border-[#27272A] text-[#F5F2E8] max-w-lg max-h-[90vh] overflow-y-auto">
              <DialogHeader><DialogTitle className="font-heading text-2xl tracking-[2px]">UPLOAD TENDER DOCUMENT</DialogTitle></DialogHeader>
              <div className="mt-4 space-y-4">
                <div data-testid="tender-drop-zone" onDrop={handleDrop} onDragOver={(e) => e.preventDefault()} className="border-2 border-dashed border-[#27272A] hover:border-[#FF5C1A] transition-colors p-8 text-center cursor-pointer" onClick={() => fileInputRef.current?.click()}>
                  <Upload size={32} className="mx-auto text-[#888880] mb-3" />
                  <p className="text-sm text-[#888880]">Drag & drop or click to upload</p>
                  <p className="text-[10px] text-[#888880] font-mono mt-1">PDF, JPG, PNG (max 10MB)</p>
                  <input ref={fileInputRef} type="file" accept=".pdf,.jpg,.jpeg,.png,.webp" onChange={handleDocUpload} className="hidden" data-testid="tender-file-input" />
                </div>
                {parseLoading && <div className="text-center py-6"><div className="inline-flex items-center gap-2 text-[#FF5C1A] font-mono text-sm"><Sparkles size={16} className="animate-spin" /> AI is parsing...</div></div>}
                {parsedData && (
                  <div className="space-y-3" data-testid="parsed-tender-preview">
                    <h4 className="font-heading text-lg tracking-[2px] text-[#FF5C1A]">EXTRACTED DATA</h4>
                    {["title", "portal", "bid_number", "state", "district", "deadline", "category", "eligibility", "description"].map((k) => (
                      <div key={k}>
                        <label className="block font-mono text-[10px] text-[#888880] tracking-[2px] uppercase mb-1">{k.replace("_", " ")}</label>
                        <input value={parsedData[k] || ""} onChange={(e) => setParsedData({ ...parsedData, [k]: e.target.value })} className={inputCls} />
                      </div>
                    ))}
                    <div>
                      <label className="block font-mono text-[10px] text-[#888880] tracking-[2px] uppercase mb-1">Value (INR)</label>
                      <input type="number" value={parsedData.value || ""} onChange={(e) => setParsedData({ ...parsedData, value: parseFloat(e.target.value) || 0 })} className={inputCls} />
                    </div>
                    <button data-testid="save-parsed-tender-btn" onClick={saveParsedTender} className="w-full bg-[#FF5C1A] text-[#0A0A08] py-3 font-mono text-xs font-bold tracking-widest uppercase hover:bg-[#FFB800] transition-colors">Save to Radar</button>
                  </div>
                )}
              </div>
            </DialogContent>
          </Dialog>

          {/* Bid Search */}
          <Dialog open={bidSearchOpen} onOpenChange={setBidSearchOpen}>
            <DialogTrigger asChild>
              <button data-testid="bid-search-btn" className="bg-[#111110] border border-[#27272A] text-[#F5F2E8] px-4 py-2.5 font-mono text-xs font-bold tracking-widest uppercase hover:border-[#FF5C1A] transition-colors flex items-center gap-2"><Hash size={14} /> Search Bid</button>
            </DialogTrigger>
            <DialogContent className="bg-[#111110] border-[#27272A] text-[#F5F2E8] max-w-lg max-h-[90vh] overflow-y-auto">
              <DialogHeader><DialogTitle className="font-heading text-2xl tracking-[2px]">SEARCH BY BID NUMBER</DialogTitle></DialogHeader>
              <form onSubmit={handleBidSearch} className="space-y-4 mt-4">
                <div><label className="block font-mono text-[10px] text-[#888880] tracking-[2px] uppercase mb-1">Bid Number</label><input data-testid="bid-number-input" value={bidSearchForm.bid_number} onChange={(e) => setBidSearchForm({ ...bidSearchForm, bid_number: e.target.value })} required className={inputCls} placeholder="GEM/2026/B/..." /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><label className="block font-mono text-[10px] text-[#888880] tracking-[2px] uppercase mb-1">Portal</label><select value={bidSearchForm.portal} onChange={(e) => setBidSearchForm({ ...bidSearchForm, portal: e.target.value })} className={inputCls}><option value="">Select</option>{portals.map((p) => <option key={p} value={p}>{p}</option>)}</select></div>
                  <div><label className="block font-mono text-[10px] text-[#888880] tracking-[2px] uppercase mb-1">State</label><select value={bidSearchForm.state} onChange={(e) => setBidSearchForm({ ...bidSearchForm, state: e.target.value })} className={inputCls}><option value="">Select</option>{states.map((s) => <option key={s} value={s}>{s}</option>)}</select></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><label className="block font-mono text-[10px] text-[#888880] tracking-[2px] uppercase mb-1">District</label><input value={bidSearchForm.district} onChange={(e) => setBidSearchForm({ ...bidSearchForm, district: e.target.value })} className={inputCls} /></div>
                  <div><label className="block font-mono text-[10px] text-[#888880] tracking-[2px] uppercase mb-1">Closing Date</label><input type="date" value={bidSearchForm.closing_date} onChange={(e) => setBidSearchForm({ ...bidSearchForm, closing_date: e.target.value })} className={inputCls} /></div>
                </div>
                <button data-testid="search-bid-submit" type="submit" disabled={bidSearching} className="w-full bg-[#FF5C1A] text-[#0A0A08] py-3 font-mono text-xs font-bold tracking-widest uppercase hover:bg-[#FFB800] transition-colors disabled:opacity-50">{bidSearching ? "Searching..." : "Search"}</button>
              </form>
              {bidSearchResult && (
                <div className="mt-4 p-4 bg-[#0A0A08] border border-[#27272A]" data-testid="bid-search-result">
                  {bidSearchResult.found ? <p className="text-sm text-[#2ECC71] font-mono">Found in your radar: {bidSearchResult.tender?.title}</p> : (
                    <div><p className="text-sm text-[#FFB800] font-mono mb-3">{bidSearchResult.message}</p>
                      <button data-testid="add-bid-manually-btn" onClick={addBidFromSearch} className="bg-[#FF5C1A] text-[#0A0A08] px-4 py-2 font-mono text-xs font-bold tracking-widest uppercase hover:bg-[#FFB800] transition-colors flex items-center gap-2"><Plus size={14} /> Add Manually</button></div>
                  )}
                </div>
              )}
            </DialogContent>
          </Dialog>

          {/* Add Tender */}
          <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
            <DialogTrigger asChild>
              <button data-testid="add-tender-btn" className="bg-[#FF5C1A] text-[#0A0A08] px-5 py-2.5 font-mono text-xs font-bold tracking-widest uppercase hover:bg-[#FFB800] transition-colors flex items-center gap-2 shadow-[4px_4px_0px_0px_rgba(0,0,0,0.5)] active:translate-y-[2px] active:shadow-none"><Plus size={16} /> Add Tender</button>
            </DialogTrigger>
            <DialogContent className="bg-[#111110] border-[#27272A] text-[#F5F2E8] max-w-lg max-h-[90vh] overflow-y-auto">
              <DialogHeader><DialogTitle className="font-heading text-2xl tracking-[2px]">ADD NEW TENDER</DialogTitle></DialogHeader>
              <form onSubmit={handleCreate} className="space-y-4 mt-4">
                <div><label className="block font-mono text-[10px] text-[#888880] tracking-[2px] uppercase mb-1">Title</label><input data-testid="tender-title-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required className={inputCls} placeholder="NHAI Road Widening..." /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><label className="block font-mono text-[10px] text-[#888880] tracking-[2px] uppercase mb-1">Category</label><select data-testid="tender-category-select" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className={inputCls}>{NICHES.map((c) => <option key={c} value={c}>{c}</option>)}<option value="General">General</option></select></div>
                  <div><label className="block font-mono text-[10px] text-[#888880] tracking-[2px] uppercase mb-1">State</label><select value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} className={inputCls}><option value="">Select</option>{states.map((s) => <option key={s} value={s}>{s}</option>)}</select></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><label className="block font-mono text-[10px] text-[#888880] tracking-[2px] uppercase mb-1">Value (INR)</label><input data-testid="tender-value-input" type="number" value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} className={inputCls} /></div>
                  <div><label className="block font-mono text-[10px] text-[#888880] tracking-[2px] uppercase mb-1">Deadline</label><input type="date" value={form.deadline} onChange={(e) => setForm({ ...form, deadline: e.target.value })} className={inputCls} /></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><label className="block font-mono text-[10px] text-[#888880] tracking-[2px] uppercase mb-1">Bid Number</label><input value={form.bid_number} onChange={(e) => setForm({ ...form, bid_number: e.target.value })} className={inputCls} /></div>
                  <div><label className="block font-mono text-[10px] text-[#888880] tracking-[2px] uppercase mb-1">Portal</label><select value={form.portal} onChange={(e) => setForm({ ...form, portal: e.target.value })} className={inputCls}><option value="">Select</option>{portals.map((p) => <option key={p} value={p}>{p}</option>)}</select></div>
                </div>
                <div><label className="block font-mono text-[10px] text-[#888880] tracking-[2px] uppercase mb-1">Description</label><textarea data-testid="tender-desc-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className={`${inputCls} h-20 resize-none`} /></div>
                <button data-testid="submit-tender-btn" type="submit" className="w-full bg-[#FF5C1A] text-[#0A0A08] py-3 font-mono text-xs font-bold tracking-widest uppercase hover:bg-[#FFB800] transition-colors">Add Tender</button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-[#111110] border border-[#27272A] p-4 space-y-3" data-testid="tender-filters">
        {userNiche && (
          <div className="flex items-center gap-3 pb-3 border-b border-[#27272A]">
            <Switch data-testid="show-all-tenders-toggle" checked={showAllTenders} onCheckedChange={setShowAllTenders} className="data-[state=checked]:bg-[#FF5C1A]" />
            <span className="font-mono text-xs text-[#888880] tracking-widest uppercase">{showAllTenders ? "All categories" : `Filtered: ${userNiche}`}</span>
          </div>
        )}
        <div className="flex flex-col md:flex-row gap-3 items-start md:items-center">
          <div className="relative flex-1 w-full md:max-w-xs">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#888880]" />
            <input data-testid="tender-search-input" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search..." className={`${inputCls} pl-10`} />
          </div>
          <div className="flex flex-wrap gap-2 items-center">
            <Select value={filterCategory} onValueChange={setFilterCategory}><SelectTrigger data-testid="filter-category" className="w-[140px] bg-[#0A0A08] border-[#27272A] text-[#F5F2E8] text-xs"><SelectValue placeholder="Category" /></SelectTrigger><SelectContent className="bg-[#111110] border-[#27272A]">{NICHES.map((c) => <SelectItem key={c} value={c} className="text-[#F5F2E8] text-xs focus:bg-[#FF5C1A]/10">{c}</SelectItem>)}</SelectContent></Select>
            <Select value={filterState} onValueChange={setFilterState}><SelectTrigger data-testid="filter-state" className="w-[140px] bg-[#0A0A08] border-[#27272A] text-[#F5F2E8] text-xs"><SelectValue placeholder="State" /></SelectTrigger><SelectContent className="bg-[#111110] border-[#27272A]">{states.map((s) => <SelectItem key={s} value={s} className="text-[#F5F2E8] text-xs focus:bg-[#FF5C1A]/10">{s}</SelectItem>)}</SelectContent></Select>
            <Select value={filterStatus} onValueChange={setFilterStatus}><SelectTrigger data-testid="filter-status" className="w-[110px] bg-[#0A0A08] border-[#27272A] text-[#F5F2E8] text-xs"><SelectValue placeholder="Status" /></SelectTrigger><SelectContent className="bg-[#111110] border-[#27272A]">{statuses.map((s) => <SelectItem key={s} value={s} className="text-[#F5F2E8] text-xs focus:bg-[#FF5C1A]/10">{s}</SelectItem>)}</SelectContent></Select>
            {(filterCategory || filterState || filterStatus) && <button data-testid="clear-filters-btn" onClick={clearFilters} className="text-[#888880] hover:text-[#E74C3C] p-1"><X size={16} /></button>}
          </div>
        </div>
      </div>

      {/* Summary Dialog */}
      <Dialog open={summaryDialogOpen} onOpenChange={setSummaryDialogOpen}>
        <DialogContent className="bg-[#111110] border-[#27272A] text-[#F5F2E8] max-w-lg">
          <DialogHeader><DialogTitle className="font-heading text-2xl tracking-[2px] flex items-center gap-2"><Sparkles size={20} className="text-[#FF5C1A]" /> TENDER SUMMARY</DialogTitle></DialogHeader>
          <div className="mt-4">{summaryLoading ? <div className="text-center py-8"><Sparkles size={24} className="mx-auto text-[#FF5C1A] animate-spin mb-3" /><p className="font-mono text-sm text-[#888880]">Analyzing...</p></div> : <div className="bg-[#0A0A08] border border-[#27272A] p-4 text-sm leading-relaxed whitespace-pre-wrap" data-testid="tender-summary-text">{summaryText}</div>}</div>
        </DialogContent>
      </Dialog>

      {/* Analyze & Prepare Bid Dialog */}
      <Dialog open={analyzeDialogOpen} onOpenChange={setAnalyzeDialogOpen}>
        <DialogContent className="bg-[#111110] border-[#27272A] text-[#F5F2E8] max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading text-2xl tracking-[2px] flex items-center gap-2"><Shield size={20} className="text-[#FF5C1A]" /> ANALYSE & PREPARE BID</DialogTitle></DialogHeader>
          {analyzeLoading ? (
            <div className="text-center py-12">
              <BarChart3 size={32} className="mx-auto text-[#FF5C1A] animate-pulse mb-4" />
              <p className="font-mono text-sm text-[#888880]">AI is analyzing the tender and matching your documents...</p>
              <p className="font-mono text-[10px] text-[#888880] mt-2">This may take 15-30 seconds</p>
            </div>
          ) : analysisResult && (
            <div className="mt-4 space-y-6" data-testid="bid-analysis-result">
              {/* Readiness Score */}
              <div className="bg-[#0A0A08] border border-[#27272A] p-6 text-center">
                <div className="font-heading text-6xl" style={{ color: analysisResult.readiness_score >= 80 ? '#2ECC71' : analysisResult.readiness_score >= 50 ? '#FFB800' : '#E74C3C' }} data-testid="readiness-score">
                  {analysisResult.readiness_score}%
                </div>
                <div className="font-mono text-xs text-[#888880] tracking-widest uppercase mt-1">Bid Readiness Score</div>
                <div className="font-mono text-sm text-[#F5F2E8] mt-2">{analysisResult.ready_count}/{analysisResult.total_required} documents ready</div>
              </div>

              {/* Work Description */}
              <div>
                <h4 className="font-heading text-lg tracking-[2px] text-[#FF5C1A] mb-2">WORK DESCRIPTION</h4>
                <p className="text-sm text-[#F5F2E8]/80 leading-relaxed">{analysisResult.analysis?.work_description}</p>
              </div>

              {/* Eligibility */}
              <div>
                <h4 className="font-heading text-lg tracking-[2px] text-[#FF5C1A] mb-2">ELIGIBILITY</h4>
                <div className="space-y-1.5 text-sm">
                  {analysisResult.analysis?.eligibility && typeof analysisResult.analysis.eligibility === 'object' && Object.entries(analysisResult.analysis.eligibility).map(([k, v]) => (
                    <div key={k} className="flex gap-2"><span className="text-[#888880] font-mono text-xs uppercase min-w-[120px]">{k.replace(/_/g, " ")}:</span><span className="text-[#F5F2E8]/80">{Array.isArray(v) ? v.join(", ") : String(v)}</span></div>
                  ))}
                </div>
              </div>

              {/* Key Dates */}
              <div>
                <h4 className="font-heading text-lg tracking-[2px] text-[#FF5C1A] mb-2">KEY DATES</h4>
                <div className="space-y-1.5 text-sm">
                  {analysisResult.analysis?.key_dates && Object.entries(analysisResult.analysis.key_dates).map(([k, v]) => (
                    <div key={k} className="flex gap-2"><span className="text-[#888880] font-mono text-xs uppercase min-w-[150px]">{k.replace(/_/g, " ")}:</span><span className="text-[#FFB800] font-mono">{v}</span></div>
                  ))}
                </div>
              </div>

              {/* Value & EMD */}
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-[#0A0A08] border border-[#27272A] p-4">
                  <div className="font-mono text-[10px] text-[#888880] tracking-widest uppercase mb-1">Estimated Value</div>
                  <div className="font-mono text-lg text-[#FFB800]">{analysisResult.analysis?.estimated_value || formatCurrency(analysisResult.tender_value)}</div>
                </div>
                <div className="bg-[#0A0A08] border border-[#27272A] p-4">
                  <div className="font-mono text-[10px] text-[#888880] tracking-widest uppercase mb-1">EMD Amount</div>
                  <div className="font-mono text-lg text-[#E74C3C]">{analysisResult.analysis?.emd_amount || "N/A"}</div>
                </div>
              </div>

              {/* Document Checklist */}
              <div>
                <h4 className="font-heading text-lg tracking-[2px] text-[#FF5C1A] mb-3">DOCUMENT CHECKLIST</h4>
                <div className="space-y-1.5">
                  {analysisResult.document_matching?.map((dm, i) => (
                    <div key={i} data-testid={`doc-match-${i}`} className={`flex items-center gap-3 px-3 py-2.5 text-sm ${dm.status === 'missing' ? 'bg-[#E74C3C]/5 border border-[#E74C3C]/20' : dm.status === 'expiring' || dm.status === 'expired' ? 'bg-[#FFB800]/5 border border-[#FFB800]/20' : 'bg-[#2ECC71]/5 border border-[#2ECC71]/20'}`}>
                      <DocStatusIcon status={dm.status} />
                      <span className="flex-1">{dm.required}</span>
                      {dm.vault_document && <span className="text-xs text-[#888880] font-mono">{dm.vault_document}</span>}
                      {dm.status === "expiring" && dm.expiry_date && <span className="text-[10px] text-[#FFB800] font-mono">Exp: {dm.expiry_date}</span>}
                    </div>
                  ))}
                </div>
              </div>

              {/* Action Plan */}
              <div>
                <h4 className="font-heading text-lg tracking-[2px] text-[#FF5C1A] mb-3">ACTION PLAN</h4>
                <div className="space-y-2">
                  {analysisResult.action_plan?.map((ap, i) => (
                    <div key={i} className={`px-3 py-2.5 text-sm flex items-center gap-2 ${ap.action === 'ready' ? 'bg-[#2ECC71]/10 text-[#2ECC71]' : ap.action === 'arrange' ? 'bg-[#E74C3C]/10 text-[#E74C3C]' : 'bg-[#FFB800]/10 text-[#FFB800]'}`}>
                      {ap.action === "arrange" && <><XCircle size={14} /> <span>Arrange: <strong>{ap.document}</strong></span></>}
                      {ap.action === "renew" && <><AlertTriangle size={14} /> <span>Renew: <strong>{ap.document}</strong> (expires {ap.expiry})</span></>}
                      {ap.action === "ready" && <><CheckCircle size={14} /> <strong>All documents ready. You can bid!</strong></>}
                    </div>
                  ))}
                </div>
              </div>

              {/* Special Conditions */}
              {analysisResult.analysis?.special_conditions?.length > 0 && (
                <div>
                  <h4 className="font-heading text-lg tracking-[2px] text-[#E74C3C] mb-2">RED FLAGS / SPECIAL CONDITIONS</h4>
                  <div className="space-y-1.5">
                    {analysisResult.analysis.special_conditions.map((s, i) => (
                      <div key={i} className="text-sm text-[#E74C3C]/80 flex items-start gap-2"><AlertTriangle size={14} className="flex-shrink-0 mt-0.5" /> {s}</div>
                    ))}
                  </div>
                </div>
              )}

              {/* Download PDF */}
              <button data-testid="download-analysis-pdf-btn" onClick={handleDownloadPdf} disabled={pdfLoading} className="w-full bg-[#FF5C1A] text-[#0A0A08] py-3 font-mono text-xs font-bold tracking-widest uppercase hover:bg-[#FFB800] transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
                <Download size={16} /> {pdfLoading ? "Generating PDF..." : "Download Summary PDF"}
              </button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Tender List */}
      {loading ? <div className="text-center py-12 text-[#888880] font-mono text-sm">Loading...</div> : filtered.length === 0 ? <div className="text-center py-12 bg-[#111110] border border-[#27272A]"><div className="text-[#888880] font-mono text-sm">No tenders found</div></div> : (
        <div className="space-y-2">
          {filtered.map((t) => (
            <div key={t.id} data-testid={`tender-item-${t.id}`} className="bg-[#111110] border border-[#27272A] p-4 md:p-5 group hover:border-[#FF5C1A]/30 transition-colors">
              <div className="flex flex-col md:flex-row md:items-center gap-3 md:gap-6">
                <div className="flex-1 min-w-0">
                  <div className="text-base font-medium truncate">{t.title}</div>
                  <div className="flex flex-wrap gap-3 mt-1.5 text-xs text-[#888880]">
                    {t.category && <span>{t.category}</span>}
                    {t.state && <span>{t.state}</span>}
                    {t.bid_number && <span className="font-mono text-[#FFB800]">{t.bid_number}</span>}
                    {t.source && <span className="font-mono">{t.source}</span>}
                  </div>
                </div>
                <div className="font-mono text-lg text-[#FFB800] flex-shrink-0">{formatCurrency(t.value)}</div>
                {t.deadline && <div className="font-mono text-xs text-[#888880] flex-shrink-0">{t.deadline}</div>}
                <Select value={t.status} onValueChange={(val) => handleStatusChange(t.id, val)}>
                  <SelectTrigger data-testid={`tender-status-${t.id}`} className={`w-[110px] border font-mono text-[10px] tracking-widest uppercase ${statusColor[t.status]} bg-transparent`}><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#111110] border-[#27272A]">{statuses.map((s) => <SelectItem key={s} value={s} className="text-[#F5F2E8] text-xs focus:bg-[#FF5C1A]/10">{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-[#27272A]/50">
                <button data-testid={`analyze-bid-${t.id}`} onClick={() => handleAnalyzeBid(t.id)} className="flex items-center gap-1.5 px-3 py-1.5 bg-[#FF5C1A]/10 text-[#FF5C1A] font-mono text-[10px] tracking-widest uppercase hover:bg-[#FF5C1A]/20 transition-colors"><Shield size={12} /> Analyse & Prepare Bid</button>
                <button data-testid={`summarize-tender-${t.id}`} onClick={() => handleSummarize(t.id)} className="flex items-center gap-1.5 px-3 py-1.5 bg-[#FFB800]/10 text-[#FFB800] font-mono text-[10px] tracking-widest uppercase hover:bg-[#FFB800]/20 transition-colors"><Sparkles size={12} /> Summarise</button>
                {t.status === "Won" && <button data-testid={`convert-to-project-${t.id}`} onClick={() => handleConvertToProject(t.id)} className="flex items-center gap-1.5 px-3 py-1.5 bg-[#2ECC71]/10 text-[#2ECC71] font-mono text-[10px] tracking-widest uppercase hover:bg-[#2ECC71]/20 transition-colors"><ArrowRight size={12} /> Convert to Project</button>}
                <button data-testid={`delete-tender-${t.id}`} onClick={() => handleDelete(t.id)} className="flex items-center gap-1.5 px-3 py-1.5 text-[#888880] hover:text-[#E74C3C] hover:bg-[#E74C3C]/10 font-mono text-[10px] tracking-widest uppercase transition-colors ml-auto"><Trash2 size={12} /> Delete</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
